import { PomodoroWidgetSettingsEntity } from "@lifeat/shared/data-models/widget/Pomodoro/PomodoroWidgetSettingsEntity";
import { getErrorMessage } from "@lifeat/shared/error/ErrorUtil";
import { GROUP_POMODORO_NODE, GROUP_POMODORO_SETTINGS_NODE, ROOMS_NODE } from "@lifeat/shared/rtdb-nodes/RtdbNodes";
import {
    PomodoroTimerType,
    StartGroupTimerWorkflowRequestPayload,
    StopGroupTimerWorkflowRequestPayload,
    TimerDurations,
} from "@lifeat/shared/widget/PomodoroTimerTypes";
import { db, functions } from "@firebase-local/firebase";
import { POMODORO_WIDGET_FUNCTIONS } from "@firebase-local/functions/ServerlessEndpoints";
import { logError } from "@service/ConsoleLoggerService";
import { PomodoroAnalyticsService } from "./PomodoroAnalyticsService";
import { PomodoroService } from "./PomodoroService";

export function HostPomodoroService(roomId: string, analytics: PomodoroAnalyticsService): PomodoroService {
    const fetchProAlarmSounds = async () => {
        try {
            const result = await functions.httpsCallable(POMODORO_WIDGET_FUNCTIONS.FetchProAlarmSounds)();
            return result.data;
        } catch (error) {
            logError(getErrorMessage(error));
        }
    };

    const updateSelectedAlarmSrc = async (value: string) => {
        const path = `${ROOMS_NODE.name}/${roomId}/${GROUP_POMODORO_NODE.name}/${GROUP_POMODORO_SETTINGS_NODE.name}/${GROUP_POMODORO_SETTINGS_NODE.children.SELECTED_ALARM_SRC}`;
        const ref = db.ref(path);
        return ref.set(value);
    };

    const fetchSettings = async (): Promise<PomodoroWidgetSettingsEntity | null> => {
        const path = `${ROOMS_NODE.name}/${roomId}/${GROUP_POMODORO_NODE.name}/${GROUP_POMODORO_SETTINGS_NODE.name}`;
        const ref = db.ref(path);
        const snapshot = await ref.once("value");
        return snapshot.val();
    };

    const updateSettings = async (value: Partial<PomodoroWidgetSettingsEntity>) => {
        const path = `${ROOMS_NODE.name}/${roomId}/${GROUP_POMODORO_NODE.name}/${GROUP_POMODORO_SETTINGS_NODE.name}`;
        const ref = db.ref(path);
        return ref.update(value);
    };

    const startServerTimer = async (roomId: string, timerDurations: TimerDurations) => {
        const payload: StartGroupTimerWorkflowRequestPayload = {
            roomId,
            timerDurations,
        };
        await functions
            .httpsCallable(POMODORO_WIDGET_FUNCTIONS.StartGroupPomodoroWorkflow)(payload)
            .catch((e) => {
                logError(e);
                alert(
                    `There was an error starting the group timer: ${e.message}.` +
                        `\n\nPlease try again or contact support.`,
                );
            });
    };

    const stopServerTimer = async (roomId: string) => {
        const payload: StopGroupTimerWorkflowRequestPayload = {
            roomId,
        };
        await functions
            .httpsCallable(POMODORO_WIDGET_FUNCTIONS.StopGroupPomodoroWorkflow)(payload)
            .catch((e) => {
                logError(e);
                alert(
                    `There was an error stopping the group timer: ${e.message}.` +
                        `\n\nPlease try again in a few moments and contact support if the issue persists.`,
                );
            });
    };

    const logTimerStartEvent = (timerType: PomodoroTimerType, fromClick: boolean) => {
        analytics.log({
            eventType: "start",
            fromClick,
            timerType,
            timerMode: "host",
        });
    };

    const logTimerEndEvent = (timerType: PomodoroTimerType) => {
        analytics.log({
            eventType: "end",
            timerType,
            timerMode: "host",
        });
    };

    return {
        fetchProAlarmSounds,
        updateSettings,
        fetchSettings,
        updateSelectedAlarmSrc,
        startServerTimer,
        stopServerTimer,
        logTimerStartEvent,
        logTimerEndEvent,
    };
}
